import { useEffect, useRef, useState } from "react";
import { listMyDebates } from "../api.js";
import { verdict } from "../verdict.js";
import Icon from "../ui/Icon.jsx";
import Booting from "./Booting.jsx";
import Broken from "./Broken.jsx";

/**
 * 내 지난 토론.
 *
 * 저장 시트가 「다른 기기에서도 이 결과를 다시 볼 수 있어요」라고 약속한다.
 * 이 화면이 그 약속이다 — 없으면 로그인의 유일한 효과가
 * 「다음 토론이 계정에 붙는다」뿐이다.
 */
export default function MyDebates({ onOpen, onHome, onPrivacy }) {
  const [items, setItems] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | more | failed
  const [err, setErr] = useState(null);
  const cursor = useRef(null);
  const [done, setDone] = useState(false);

  async function load(reset) {
    setState(reset ? "loading" : "more");
    try {
      const r = await listMyDebates({ cursor: reset ? null : cursor.current });
      cursor.current = r.cursor;
      setDone(r.done);
      setItems((prev) => (reset ? r.debates : [...prev, ...r.debates]));
      setState("ready");
    } catch (e) {
      setErr(e);
      setState("failed");
    }
  }

  useEffect(() => { load(true); }, []);

  if (state === "failed") return <Broken err={err} onRetry={() => load(true)} />;
  if (state === "loading" && !items.length) return <Booting />;

  return (
    <main className="screen" style={{ minHeight: "100vh" }}>
      <header className="bar">
        <button type="button" className="icon-btn" aria-label="처음으로" onClick={onHome}>
          <Icon name="back" />
        </button>
        <span className="grow" />
        <button type="button" className="pill" style={{ minHeight: 36 }} onClick={onPrivacy}>
          개인정보 설정
        </button>
      </header>

      <h1 className="title">내 기록</h1>

      {items.length === 0 ? (
        <section className="card raised" style={{ borderRadius: 4, padding: "26px 22px", marginTop: 8 }}>
          <h2 className="d" style={{ fontSize: 28, lineHeight: 1.2 }}>아직 한 토론이 없어요</h2>
          <p style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.6 }}>
            주제를 하나 골라 여섯 번 주고받으면 여기 쌓여요.
          </p>
          <button type="button" className="btn primary" onClick={onHome}>주제 고르러 가기</button>
        </section>
      ) : (
        <ul className="list">
          {items.map((d) => <Row key={d.id} d={d} onOpen={onOpen} />)}
        </ul>
      )}

      {!done && items.length > 0 && (
        <button type="button" className="btn" onClick={() => load(false)} disabled={state === "more"}>
          {state === "more" ? "불러오는 중…" : "더 보기"}
        </button>
      )}
    </main>
  );
}

function Row({ d, onOpen }) {
  const finished = d.status === "completed" && d.outcome;
  const when = d.createdAt?.toDate?.();
  const date = when
    ? `${when.getFullYear()}.${String(when.getMonth() + 1).padStart(2, "0")}.${String(when.getDate()).padStart(2, "0")}`
    : "";

  return (
    <li>
      <button type="button" className="row-link"
        onClick={() => finished && onOpen(d)}
        aria-disabled={!finished}
        style={{
          flexDirection: "column", alignItems: "stretch", gap: 8,
          padding: "16px 18px", minHeight: 0,
          cursor: finished ? "pointer" : "default",
        }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {/* 어느 게 공개돼 있는지 목록에서 보여야 내릴 생각을 한다. */}
          {d.isPublic && <span className="pill" style={{ fontSize: 11 }}>아카이브 공개 중</span>}
          <span className="note">{date}</span>
          <span className="grow" />
          {finished ? (
            <span className="num" style={{ fontSize: 13, fontWeight: 800 }}>
              확신도 {d.convictionStart} → {d.convictionEnd}
            </span>
          ) : (
            <span className="pill mark" style={{ fontSize: 12 }}>{d.turnCount ?? 0}/6 · 하던 중</span>
          )}
        </span>

        <span style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.45, textWrap: "pretty" }}>
          {d.topicSnapshot?.title ?? "(주제 없음)"}
        </span>

        <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span className="pill" style={{ fontSize: 12 }}>나 · {d.stanceLabel}</span>
          {finished && (
            <span className="note">
              {verdict({
                convictionStart: d.convictionStart,
                convictionEnd: d.convictionEnd,
                evasions: d.evasions ?? 0,
              })}
            </span>
          )}
          <span className="grow" />
          {finished && <Icon name="next" size={18} />}
        </span>
      </button>
    </li>
  );
}
